import { fetchDeploymentBankId, fetchBranches, updateDeployment, checkDeploymentConflict, deleteDeployment, type Branch } from '../../../service/deploymentsService';
import { fetchCompaniesData } from '../../../../companies/service/companiesService';
import type { Company } from '../../../../companies/service/companiesService';

/**
 * Fetch available branches for the current user
 */
export async function fetchAvailableBranches(uid: string, isBankUser: boolean = false): Promise<Branch[]> {
    const bankId = await fetchDeploymentBankId(uid, isBankUser);
    if (!bankId) {
        return [];
    }
    return await fetchBranches(bankId, isBankUser ? undefined : uid);
}

/**
 * Fetch available companies for the current user
 */
export async function fetchAvailableCompanies(uid: string, isBankUser: boolean = false): Promise<Company[]> {
    return await fetchCompaniesData(uid, isBankUser);
}

/**
 * Update an existing deployment
 */
export async function updateDeploymentData(
    uid: string,
    isBankUser: boolean,
    bankId: string,
    deploymentId: string,
    companiesId: string
): Promise<{ success: boolean; conflict?: { existingDeployment: any; existingCompany: Company } }> {
    try {
        // Get the deployment to check branchId
        const { getDoc, doc } = await import('firebase/firestore');
        const { db } = await import('../../../../../firebase/config');
        const deploymentRef = doc(db, "banks", bankId, "companyDeployment", deploymentId);
        const deploymentDoc = await getDoc(deploymentRef);
        
        if (!deploymentDoc.exists()) {
            throw new Error('Deployment not found');
        }

        const deploymentData = deploymentDoc.data();
        const branchId = deploymentData.branchId;

        // Check for conflicts (excluding current deployment)
        const conflictCheck = await checkDeploymentConflict(bankId, branchId, companiesId, deploymentId);
        if (conflictCheck.hasConflict) {
            return {
                success: false,
                conflict: {
                    existingDeployment: conflictCheck.existingDeployment,
                    existingCompany: conflictCheck.existingCompany!
                }
            };
        }

        // Update deployment
        await updateDeployment(bankId, deploymentId, companiesId);
        return { success: true };
    } catch (error) {
        console.error('Error updating deployment:', error);
        throw error;
    }
}

/**
 * Replace existing deployment (used when resolving conflicts)
 */
export async function replaceDeploymentData(
    uid: string,
    isBankUser: boolean,
    bankId: string,
    deploymentId: string,
    companiesId: string,
    existingDeploymentId: string
): Promise<void> {
    try {
        // Delete existing conflicting deployment
        await deleteDeployment(bankId, existingDeploymentId);

        // Update current deployment
        await updateDeployment(bankId, deploymentId, companiesId);
    } catch (error) {
        console.error('Error replacing deployment:', error);
        throw error;
    }
}

